import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
  getFilteredRowModel,
  ColumnSizingState,
  RowSelectionState,
  HeaderContext,
  CellContext,
  ColumnPinningState,
} from "@tanstack/react-table";
import React, { Fragment, useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  SortDirection,
  parseSortDirection,
  TableHeaderType,
  TableHeaderTypeBase,
  TableProps,
  TableRowItem,
} from "./types";
import { ScrollArea, Tooltip, Typography } from "@altimateai/lego";
import { InfoGray } from "@/assets/icons";
import { useColumnPickerContext } from "../column/types";
import ColumnPicker from "../column/ColumnPicker";
import { setTableHeaders, setSelectedColumns, setApplicableFor } from "../column/columnPickerSlice";
import { useDatatableContext } from "./contexts/DatatableContextProvider";
import DataTablePortal from "./components/DataTablePortal";
import { Checkbox } from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { Link } from "react-router-dom";
import SortIcon from "./SortIcon";
import TableFilter from "./TableFilter";
import AddFiltersButton from "./AddFiltersButton";
import { calculateDynamicMinSize, calculateDynamicMinWidth, getCommonPinningStyles } from "./utils";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";

const DataTable = <TData extends TableRowItem = TableRowItem>({
  header,
  data,
  onRowClick,
  getRowUrl,
  getRowState,
  resetPage,
  applicableFor,
  headerClass = "",
  tableClass = "",
  tableBodyClass = "",
  columnPickerPortal,
  columnPickerProps,
  idKey,
  minWidth = 1024,
  enableRowSelection = false,
  onRowSelect,
  isLoading = false,
  portals,
  onFiltersChange,
  openedAccordion,
  accordionComponent,
  defaultSort,
  disableColumnPinning = false,
  disableDynamicHeight = false,
  clearFiltersKeysToPreserve,
}: TableProps<TData>) => {
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({});
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const {
    state: { columns },
    dispatch,
  } = useColumnPickerContext();

  // Auto-register pagination filters
  const { registerFilters } = useDatatableContext();

  // Keep refs for use in effects without depending on identity
  const headerRef = useRef(header);
  headerRef.current = header;
  const updateSearchParamsRef = useRef(updateSearchParamsByKey);
  updateSearchParamsRef.current = updateSearchParamsByKey;

  // --- Sort value derivation ---
  //
  // DataTable supports two sort modes. In both cases the internal `sorting` state
  // is the single source of truth used to render the SortIcon and drive TanStack Table.
  //
  // 1. URL-based sorting (recommended) — when a column defines `sortSearchKey`:
  //    The sort value is derived automatically from URL search params.
  //    DataTable reads `searchParams.get(sortSearchKey)` to find which column is
  //    sorted, matches it against each column's `sortParamValue ?? uid`, and reads
  //    `searchParams.get(sortOrderSearchKey ?? "sortOrder")` for the direction.
  //    Parent components do NOT need to pass `sort.value` — it is ignored.
  //    This effect also re-syncs on browser back/forward navigation.
  //
  // 2. Client-side sorting (legacy) — when no column has `sortSearchKey`:
  //    The sort value is read once from `sort.value` on mount.
  //    Parent components must pass `sort.value` and use `sort.onChange` to track state.

  // Mode 1: Sync sorting state with URL search params
  useEffect(() => {
    const currentHeader = headerRef.current;
    const sortableCol = currentHeader.find(col => col.sort?.sortSearchKey);
    if (!sortableCol?.sort?.sortSearchKey) return;

    const searchKey = sortableCol.sort.sortSearchKey;
    const orderKey = sortableCol.sort.sortOrderSearchKey ?? "sortOrder";
    const sortParam = searchParams.get(searchKey);
    const orderParam = parseSortDirection(searchParams.get(orderKey));

    if (sortParam && orderParam) {
      // Find the column whose sortParamValue (or uid) matches the URL param value
      const matchingCol = currentHeader.find(col => {
        if (!col.sort) return false;
        return (col.sort.sortParamValue ?? col.uid) === sortParam;
      });
      if (matchingCol) {
        setSorting(prev => {
          if (
            prev.length === 1 &&
            prev[0].id === matchingCol.uid &&
            prev[0].desc === (orderParam === "desc")
          ) {
            return prev;
          }
          return [{ id: matchingCol.uid, desc: orderParam === "desc" }];
        });
        return;
      }
    }

    // No valid sort in URL — fall back to defaultSort for internal state (no URL write)
    if (defaultSort) {
      const defaultCol = currentHeader.find(col => {
        if (!col.sort) return false;
        return (col.sort.sortParamValue ?? col.uid) === defaultSort.sortParamValue;
      });
      if (defaultCol) {
        const isDesc = defaultSort.order === "desc";
        setSorting(prev => {
          if (prev.length === 1 && prev[0].id === defaultCol.uid && prev[0].desc === isDesc) {
            return prev;
          }
          return [{ id: defaultCol.uid, desc: isDesc }];
        });
        return;
      }
    }

    // No default — clear sorting
    setSorting(prev => (prev.length === 0 ? prev : []));
  }, [searchParams, defaultSort]);

  // Mode 2: Initialize sorting from sort.value (client-side, no URL params)
  useEffect(() => {
    if (header.some(col => col.sort?.sortSearchKey)) return;
    const initialSort = header.find(col => col.sort?.value);
    if (initialSort) {
      setSorting([{ id: initialSort.uid, desc: initialSort.sort!.value === "desc" }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Register pagination filters once per DataTable instance
    registerFilters({
      page: {
        label: "Page",
        defaultValue: 0,
        isArray: false,
      },
      pageSize: {
        label: "Page Size",
        defaultValue: 20,
        isArray: false,
      },
    });
  }, [registerFilters]);

  useEffect(() => {
    if (columnPickerPortal) {
      dispatch(
        setTableHeaders(
          header.map(h => ({
            label: typeof h.label === "string" ? h.label : h.uid,
            value: h.uid,
            width: typeof h.width === "number" ? h.width : undefined,
          }))
        )
      );
      dispatch(setSelectedColumns(new Set(header.map(h => h.uid))));
      if (applicableFor) {
        dispatch(setApplicableFor(applicableFor));
      }
    }
  }, [header, columnPickerPortal, dispatch, applicableFor]);

  const firstColUid = header[0]?.uid;
  // Set up column pinning for the first column
  useEffect(() => {
    // disable column pinning in test to avoid flakiness in popovers in rows
    if (process.env.NODE_ENV === "test") return;
    if (header.length === 0) return;

    // opt-out: callers like the Priority tab don't want the leftmost
    // column pinned (the table is short enough that horizontal scroll is
    // acceptable and a frozen rank column would be visual noise).
    if (disableColumnPinning) {
      setColumnPinning({});
      return;
    }

    // if row selection is enabled, pin the selection column and the first data column. ex: discover or mcp memories page
    if (enableRowSelection) {
      setColumnPinning({ left: ["rowSelection", firstColUid] });
      return;
    }

    // otherwise, just pin the first data column
    setColumnPinning({ left: [firstColUid] });
  }, [enableRowSelection, header.length, firstColUid, disableColumnPinning]);

  // Calculate dynamic height based on available space
  // If we add useCallback here, it stops working on mount for some reason
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateHeight = () => {
    // Callers that don't want the 500px floor / viewport-fill behavior can
    // opt out — the table then renders at its natural content height.
    if (disableDynamicHeight) return;
    if (tableContainerRef.current?.parentElement) {
      /**
       * usecases
       * - no of rows is less, so table will not reach threshold of 500px
       * - no data - empty state
       * - no of rows is more, so table will reach threshold of 500px and more
       * - handle all these cases during resize as well
       */
      const parent = tableContainerRef.current.parentElement;
      // Only apply dynamic height if there's data to display
      if (data.items.length > 0) {
        const rect = parent.getBoundingClientRect();
        // Once the table has been scrolled above the viewport top, rect.top goes
        // negative and a viewport-relative recompute would grow the height the
        // further you've scrolled (resize/re-render can fire while scrolled).
        // Keep the height computed while it was in view; fall through only on the
        // first pass, before any height is set, so it is never left unbounded.
        if (rect.top < 0 && parent.style.height && parent.style.height !== "auto") {
          return;
        }
        const paginationHeight = 45; // Height of pagination component below table
        const bottomPadding = 40; // Additional padding from window bottom
        // Floor the offset at 0 as a safety net for that first scrolled pass.
        const distanceFromTop = Math.max(rect.top, 0);
        const availableHeight =
          window.innerHeight - distanceFromTop - paginationHeight - bottomPadding;

        // Only expand if available space is significantly greater than natural content size
        const contentHeight = parent.scrollHeight;

        if (contentHeight < 500) {
          parent.style.height = "auto";
          return;
        }

        if (availableHeight > contentHeight) {
          parent.style.height = `${availableHeight}px`;
          return;
        }

        parent.style.height = `${Math.max(500, availableHeight)}px`;
      } else {
        parent.style.height = "auto";
      }
    }
  };

  // Process column widths - now using container width for percentage calculations
  const processColumnWidth = (width?: TableHeaderType["width"]): number | undefined => {
    if (!width) return undefined; // Let TanStack handle auto-distribution
    if (typeof width === "number") return width;
    if (width.endsWith("px")) return parseInt(width);
    if (width.endsWith("%")) {
      const containerWidth = tableContainerRef.current?.clientWidth ?? 800;
      return (parseInt(width) / 100) * containerWidth;
    }
    return undefined;
  };

  // Recalculate column sizes when container changes
  useEffect(() => {
    const handleResize = () => {
      // Handle column sizing
      const newColumnSizing: ColumnSizingState = {};
      header.forEach(col => {
        if (col.width?.toString().endsWith("%")) {
          const size = processColumnWidth(col.width);
          if (size) {
            newColumnSizing[col.uid] = size;
          }
        }
      });
      setColumnSizing(prev => ({ ...prev, ...newColumnSizing }));
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [header]);

  // Check if horizontal scroll is available
  const checkHorizontalScroll = useCallback(() => {
    if (tableContainerRef.current) {
      const container = tableContainerRef.current.parentElement;
      if (container) {
        const hasScroll = container.scrollWidth > container.clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    }
  }, []);

  // Only handle height calculation on window resize, not on mount
  useEffect(() => {
    const handleWindowResize = () => {
      calculateHeight();
      checkHorizontalScroll();
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [calculateHeight, checkHorizontalScroll]);

  // Calculate dynamic minWidth based on visible columns
  const dynamicMinWidth = useMemo(() => {
    const displayHeaders = header.filter(col => (columnPickerPortal ? columns.has(col.uid) : true));
    return calculateDynamicMinWidth(displayHeaders, minWidth, header.length);
  }, [header, columns, columnPickerPortal, minWidth]);

  const tableColumns = useMemo<ColumnDef<TData>[]>(() => {
    const baseColumns = header
      .filter(col => (columnPickerPortal ? columns.has(col.uid) : true))
      .map(col => {
        // Check if this column is pinned
        const isPinned =
          columnPinning.left?.includes(col.uid) || columnPinning.right?.includes(col.uid);

        return {
          id: col.uid,
          accessorFn: (row: TData) => row,
          size: processColumnWidth(col.width),
          minSize: calculateDynamicMinSize(col.width, minWidth, col.noFloor),
          maxSize: 1000,
          header: ({ column }: HeaderContext<TData, unknown>) => (
            <Stack align={"center"} gap={"sm"} justify={"between"}>
              <Typography
                variant="table-header"
                weight="medium"
                className="al-text-gray-500 al-tracking-wider"
              >
                {col.label}
              </Typography>
              <Stack align={"center"} gap={"sm"}>
                {col.tooltipInfo && (
                  <Tooltip title={col.tooltipInfo}>
                    <span>
                      <InfoGray />
                    </span>
                  </Tooltip>
                )}
                {col.filter && !portals?.filter && (
                  <TableFilter
                    filter={col.filter}
                    label={col.label as string}
                    resetPage={resetPage}
                    // For first column, show the filter in right side
                    // https://altimateai.atlassian.net/browse/AI-3756
                    popoverContentProps={
                      columnPinning.left?.includes(col.uid)
                        ? {
                            avoidCollisions: true,
                            side: "right",
                            className: "al-max-w-80",
                          }
                        : {
                            className: "al-max-w-80",
                          }
                    }
                  />
                )}
                {/* Sort direction is derived from the internal `sorting` state,
                    which is synced from URL params (mode 1) or sort.value (mode 2).
                    See the "Sort value derivation" comment above. */}
                {col.sort && (
                  <SortIcon
                    sorting={
                      sorting.find(s => s.id === col.uid)?.desc === true
                        ? "desc"
                        : sorting.some(s => s.id === col.uid)
                          ? "asc"
                          : false
                    }
                    onSort={column.getToggleSortingHandler()}
                  />
                )}
              </Stack>
            </Stack>
          ),
          cell: ({ row }: CellContext<TData, unknown>) => col.render(row.original),
          enableSorting: Boolean(col.sort),
          enableResizing: !isPinned, // Disable resizing for pinned columns
        };
      });

    if (enableRowSelection) {
      const isRowSelectionPinned =
        columnPinning.left?.includes("rowSelection") ||
        columnPinning.right?.includes("rowSelection");

      const rowSelectionColumn: ColumnDef<TData> = {
        id: "rowSelection",
        size: 24,
        enableResizing: !isRowSelectionPinned, // Disable resizing if pinned
        header: ({ table }) => (
          <Stack align={"center"} justify={"center"}>
            <Checkbox
              checked={
                table.getIsAllRowsSelected()
                  ? true
                  : table.getIsSomeRowsSelected()
                    ? "indeterminate"
                    : false
              }
              onCheckedChange={(checked: boolean | "indeterminate") => {
                if (checked === true || checked === "indeterminate") {
                  table.toggleAllRowsSelected(true);
                } else {
                  table.toggleAllRowsSelected(false);
                }
              }}
            />
          </Stack>
        ),
        cell: ({ row }) => (
          <Stack align={"center"} justify={"center"}>
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={row.getToggleSelectedHandler()}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </Stack>
        ),
      };

      return [rowSelectionColumn, ...baseColumns];
    }

    return baseColumns;
  }, [
    header,
    columns,
    columnPickerPortal,
    resetPage,
    enableRowSelection,
    portals?.filter,
    minWidth,
    columnPinning,
    sorting,
  ]);

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSort = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSort);

    if (newSort.length > 0) {
      const sortedColumn = header.find(col => col.uid === newSort[0].id);
      if (sortedColumn?.sort) {
        const direction: SortDirection = newSort[0].desc ? "desc" : "asc";
        if (sortedColumn.sort.sortSearchKey) {
          const sortValue = sortedColumn.sort.sortParamValue ?? sortedColumn.uid;
          const searchKey = sortedColumn.sort.sortSearchKey;
          const orderKey = sortedColumn.sort.sortOrderSearchKey ?? "sortOrder";
          // Combine sort params and page reset in a single URL update to avoid
          // conflicts from multiple setSearchParams calls in different hook instances
          updateSearchParamsByKey({
            [searchKey]: sortValue,
            [orderKey]: direction,
            page: undefined,
          });
        } else {
          resetPage?.();
        }
        sortedColumn.sort.onChange?.(direction);
      }
    } else if (sorting.length > 0) {
      const previouslySortedColumn = header.find(col => col.uid === sorting[0].id);
      if (previouslySortedColumn?.sort) {
        if (previouslySortedColumn.sort.sortSearchKey) {
          const searchKey = previouslySortedColumn.sort.sortSearchKey;
          const orderKey = previouslySortedColumn.sort.sortOrderSearchKey ?? "sortOrder";
          if (defaultSort) {
            // Reset to default sort instead of removing params
            updateSearchParamsByKey({
              [searchKey]: defaultSort.sortParamValue,
              [orderKey]: defaultSort.order,
              page: undefined,
            });
          } else {
            deleteSearchParamsByKeyValue({
              [searchKey]: undefined,
              [orderKey]: undefined,
              page: undefined,
            });
          }
        } else {
          resetPage?.();
        }
        previouslySortedColumn.sort.onChange?.("");
      }
    }
  };

  const table = useReactTable({
    data: data.items,
    columns: tableColumns,
    state: {
      sorting,
      columnSizing,
      rowSelection,
      columnPinning,
    },
    enableRowSelection,
    enableColumnPinning: true,
    enableColumnResizing: hasHorizontalScroll,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 1000,
    },
  });

  useEffect(() => {
    if (onRowSelect) {
      const allRows = table.getRowModel().rows;
      const rows = Object.keys(rowSelection)
        .map(id => {
          if (rowSelection[id]) {
            return allRows[parseInt(id, 10)].original;
          } else {
            return null;
          }
        })
        .filter((row): row is TData => row !== null);
      onRowSelect(rows);
    }
  }, [rowSelection, onRowSelect, table]);

  // Check horizontal scroll when data or columns change
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated
    const timeoutId = setTimeout(() => {
      checkHorizontalScroll();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [data, columns, columnSizing, checkHorizontalScroll]);

  return (
    <>
      <DataTablePortal id={columnPickerPortal}>
        <ColumnPicker {...columnPickerProps} />
      </DataTablePortal>
      <AddFiltersButton
        portals={portals}
        headers={header as TableHeaderTypeBase[]}
        onFiltersChange={onFiltersChange}
        applicableFor={applicableFor}
        clearFiltersKeysToPreserve={clearFiltersKeysToPreserve}
      />
      <ScrollArea
        className="al-w-full al-overflow-x-auto al-border al-border-gray-200 al-rounded-md"
        ref={calculateHeight}
        type="auto"
      >
        <div data-testid="datatable-scroll-content">
          <div
            ref={tableContainerRef}
            className="al-w-full al-@container"
            style={{ minWidth: dynamicMinWidth }}
          >
            <table
              className={`al-w-full al-divide-y al-divide-gray-200 ${tableClass}`}
              style={{
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <thead className={`al-bg-gray-50 al-sticky al-top-0 al-z-10 ${headerClass}`}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className={`
                        al-px-4 al-py-3 al-text-left
                        al-break-words al-whitespace-normal al-relative al-group
                        ${header.column.getIsResizing() ? "al-select-none" : ""}
                        ${header.column.getIsPinned() ? "al-bg-gray-50" : ""}
                      `}
                        style={{
                          ...getCommonPinningStyles(header.column),
                          width: header.column.columnDef.size ? `${header.getSize()}px` : "auto",
                        }}
                      >
                        <div className="al-break-words al-whitespace-normal">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        {hasHorizontalScroll && !header.column.getIsPinned() && (
                          <button
                            type="button"
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            aria-label="Resize column"
                            className={`
                            al-absolute al-right-0 al-top-0 al-h-full al-w-1 !al-cursor-col-resize al-select-none al-touch-none
                            al-bg-gray-200 al-opacity-0 group-hover:al-opacity-100 al-border-none al-p-0
                          `}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {!isLoading && (
                <tbody className={`al-bg-white al-divide-y al-divide-gray-200 ${tableBodyClass}`}>
                  {table.getRowModel().rows.map(row => {
                    const rowId = idKey
                      ? typeof idKey === "function"
                        ? idKey(row.original)
                        : (row.original[idKey] as string | number)
                      : ((row.original.id as string | number) ?? row.index);

                    const rowUrl = getRowUrl ? getRowUrl(row.original) : null;
                    const colSpan = row.getVisibleCells().length;
                    const showAccordion = accordionComponent && openedAccordion === String(rowId);

                    if (rowUrl) {
                      return (
                        <Fragment key={rowId}>
                          <tr className="hover:al-bg-gray-50">
                            {row.getVisibleCells().map((cell, cellIndex) => {
                              const isClickableColumn = enableRowSelection
                                ? cellIndex === 1
                                : cellIndex === 0;
                              return (
                                <td
                                  key={cell.id}
                                  className={`
                                al-px-3 al-py-2 al-text-sm al-text-gray-900 al-text-[13px] al-p-0
                                al-border-b al-border-gray-100
                                ${cell.column.getIsPinned() ? "al-bg-white" : ""}
                                ${isClickableColumn && onRowClick ? "al-cursor-pointer" : ""}
                              `}
                                  style={{
                                    ...getCommonPinningStyles(cell.column),
                                    width: cell.column.columnDef.size
                                      ? `${cell.column.getSize()}px`
                                      : "auto",
                                  }}
                                  onClick={
                                    isClickableColumn && onRowClick
                                      ? () => onRowClick(row.original)
                                      : undefined
                                  }
                                >
                                  <Link
                                    to={rowUrl}
                                    state={getRowState ? getRowState(row.original) : undefined}
                                    className="al-block al-w-full al-h-full al-px-3 al-py-2 al-text-gray-900 al-no-underline hover:al-text-gray-900 hover:al-no-underline"
                                  >
                                    <div className="al-break-words al-whitespace-normal">
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </div>
                                  </Link>
                                </td>
                              );
                            })}
                          </tr>
                          {showAccordion && (
                            <tr>
                              <td colSpan={colSpan} className="al-p-4">
                                {accordionComponent(row.original)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    } else {
                      return (
                        <Fragment key={rowId}>
                          <tr className="hover:al-bg-gray-50">
                            {row.getVisibleCells().map((cell, cellIndex) => {
                              const isClickableColumn = enableRowSelection
                                ? cellIndex === 1
                                : cellIndex === 0;
                              return (
                                <td
                                  key={cell.id}
                                  className={`
                                al-px-3 al-py-2 al-text-sm al-text-gray-900 al-text-[13px]
                                al-border-b al-border-gray-100
                                ${cell.column.getIsPinned() ? "al-bg-white" : ""}
                                ${isClickableColumn && onRowClick ? "al-cursor-pointer" : ""}
                              `}
                                  style={{
                                    ...getCommonPinningStyles(cell.column),
                                    width: cell.column.columnDef.size
                                      ? `${cell.column.getSize()}px`
                                      : "auto",
                                  }}
                                  onClick={
                                    isClickableColumn && onRowClick
                                      ? () => onRowClick(row.original)
                                      : undefined
                                  }
                                >
                                  <div className="al-break-words al-whitespace-normal">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          {showAccordion && (
                            <tr>
                              <td colSpan={colSpan} className="al-p-4">
                                {accordionComponent(row.original)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    }
                  })}
                </tbody>
              )}
            </table>
          </div>
          {isLoading && (
            <Stack
              justify={"center"}
              align={"center"}
              className={"al-min-h-32 al-w-full al-py-6"}
              role="alert"
              aria-label="Loading..."
            >
              <Spinner size={"md"} />
              Loading...
            </Stack>
          )}
        </div>
      </ScrollArea>
    </>
  );
};

export default DataTable;
